import DOMPurify from 'dompurify';
/**
 * Sanitiza conteúdo HTML removendo scripts, eventos JavaScript e elementos perigosos
 * Mantém apenas tags e atributos seguros para emails HTML
 *
 * @param htmlContent - String HTML a ser sanitizada
 * @returns HTML sanitizado e seguro
 */
export const sanitizeHtml = (htmlContent) => {
    const cleanHtml = DOMPurify.sanitize(htmlContent, {
        ALLOWED_TAGS: [
            // Estrutura HTML
            'html',
            'head',
            'body',
            'title',
            'meta',
            // Texto e formatação
            'p',
            'br',
            'strong',
            'em',
            'u',
            'b',
            'i',
            's',
            'strike',
            'del',
            // Cabeçalhos
            'h1',
            'h2',
            'h3',
            'h4',
            'h5',
            'h6',
            // Listas
            'ul',
            'ol',
            'li',
            // Links e imagens
            'a',
            'img',
            'map',
            'area',
            // Tabelas
            'table',
            'thead',
            'tbody',
            'tfoot',
            'tr',
            'td',
            'th',
            'caption',
            // Containers
            'div',
            'span',
            'section',
            'article',
            'header',
            'footer',
            'main',
            // Estilos e outros
            'style',
            'center',
            'font',
            'hr',
            'blockquote',
            'pre',
            'code',
        ],
        ALLOWED_ATTR: [
            // Links e recursos
            'href',
            'src',
            'alt',
            'title',
            'target',
            // Identificação e estilo
            'class',
            'id',
            'style',
            // Dimensões e alinhamento
            'width',
            'height',
            'align',
            'valign',
            // Tabelas
            'border',
            'cellpadding',
            'cellspacing',
            'colspan',
            'rowspan',
            // Cores e fontes
            'bgcolor',
            'color',
            'size',
            'face',
            // Image maps
            'usemap',
            'name',
            'coords',
            'shape',
            // HTML e Meta tags
            'lang',
            'xmlns',
            'http-equiv',
            'content',
            'charset',
            'type',
        ],
        ALLOW_DATA_ATTR: false,
        ADD_URI_SAFE_ATTR: ['src', 'href'],
        // Permitir URLs HTTPS, HTTP, mailto, tel, data URIs
        ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp|data):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
        FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button'],
        FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onmouseout', 'onfocus', 'onblur'],
        WHOLE_DOCUMENT: true,
        RETURN_DOM: false,
        RETURN_DOM_FRAGMENT: false,
    });
    // Se o HTML original tinha DOCTYPE, adiciona de volta
    if (htmlContent.trim().toLowerCase().startsWith('<!doctype') &&
        !cleanHtml.trim().toLowerCase().startsWith('<!doctype')) {
        const doctypeMatch = htmlContent.match(/<!DOCTYPE[^>]*>/i);
        if (doctypeMatch) {
            return doctypeMatch[0] + '\n' + cleanHtml;
        }
    }
    return cleanHtml;
};
/**
 * Valida se um arquivo tem extensão HTML válida
 *
 * @param filename - Nome do arquivo a ser validado
 * @returns true se a extensão for .html ou .htm
 */
export const isValidHtmlFile = (filename) => {
    const validExtensions = ['.html', '.htm'];
    const extension = filename.toLowerCase().substring(filename.lastIndexOf('.'));
    return validExtensions.includes(extension);
};
/**
 * Verifica se o HTML contém elementos ou atributos perigosos
 *
 * @param htmlContent - Conteúdo HTML a ser verificado
 * @returns true se contiver elementos perigosos
 */
export const hasUnsafeContent = (htmlContent) => {
    const dangerousPatterns = [
        /<script[^>]*>.*?<\/script>/gis,
        /<script[^>]*>/gi,
        // Atributos de eventos JavaScript específicos
        /\s(onclick|ondblclick|onmousedown|onmouseup|onmouseover|onmousemove|onmouseout|onmouseenter|onmouseleave)\s*=/gi,
        /\s(onload|onunload|onchange|onsubmit|onreset|onselect|onblur|onfocus|onkeydown|onkeypress|onkeyup|onerror|onabort)\s*=/gi,
        /javascript:/gi,
        /<iframe[^>]*>/gi,
        /<object[^>]*>/gi,
        /<embed[^>]*>/gi,
        /<form[^>]*>/gi, // formulários
    ];
    return dangerousPatterns.some(pattern => pattern.test(htmlContent));
};
